import { styled } from "@linaria/react"
import { useEffect, useRef, useState } from "react"


import { Button } from "@/components/Button/Button.tsx"
import { Dialog } from "@/components/Dialog/Dialog.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Input } from "@/components/Input/Input.tsx"
import { InputLikeTextarea } from "@/components/ResizeableTextarea/ResizeableTextarea.tsx"
import { SECRET_VALUE_PLACEHOLDER } from "@/lib/displayUtils.ts"
import { ellipsize, smallFontSize } from "@/styles/textStyles.ts"

import type { ChangeEvent } from "react"

export interface AddSecretDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onAddSecret: (key: string, description: string, value: string) => Promise<void>
}

export const AddSecretDialog = ({ isOpen, onOpenChange, onAddSecret }: AddSecretDialogProps) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
      trigger={null}
      title="Add New Secret"
      description="Add a new runtime secret for the project"
    >
      <AddSecretDialogContent isOpen={isOpen} onOpenChange={onOpenChange} onAddSecret={onAddSecret} />
    </Dialog>
  )
}

const AddSecretDialogContent = ({ isOpen, onOpenChange, onAddSecret }: AddSecretDialogProps) => {
  const [key, setKey] = useState("")
  const [description, setDescription] = useState("")
  const [value, setValue] = useState("")

  const [showValue, setShowValue] = useState(false)

  const [keyError, setKeyError] = useState<string | null>(null)
  const [valueError, setValueError] = useState<string | null>(null)

  const [submitInProgress, setSubmitInProgress] = useState(false)

  const keyInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setTimeout(() => {
      keyInputRef.current?.focus()
    }, 100)
  }, [isOpen])

  const handleKeyChange = (e: ChangeEvent<HTMLInputElement>) => {
    setKey(e.target.value)
    setKeyError(null)
  }

  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value)
  }

  const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    setValueError(null)
  }

  const handleSubmit = async () => {
    if (!key.trim()) {
      setKeyError("Secret key is required")
      return
    }

    if (!value) {
      setValueError("Secret value is required")
      return
    }

    try {
      setSubmitInProgress(true)

      await onAddSecret(key.trim(), description.trim(), value)

      // Reset form
      setKey("")
      setDescription("")
      setValue("")

      setShowValue(false)

      setKeyError(null)
      setValueError(null)

      // Close dialog
      onOpenChange(false)
    } catch {
      setValueError("Failed to add secret")
    } finally {
      setSubmitInProgress(false)
    }
  }

  return (
    <Vertical gap="16px" width="100%">
      <FormField>
        <label htmlFor="secret-key">Key</label>
        <Input
          id="secret-key"
          ref={keyInputRef}
          value={key}
          onChange={handleKeyChange}
          placeholder="Enter secret key"
          autoFocus
        />
        {keyError && <ErrorText>{keyError}</ErrorText>}
      </FormField>

      <FormField>
        <label htmlFor="secret-description">Description (optional)</label>
        <InputLikeTextarea
          id="secret-description"
          value={description}
          onChange={handleDescriptionChange}
          placeholder="Enter a description for this secret"
          rows={3}
        />
      </FormField>

      <FormField>
        <label htmlFor="secret-value">Value</label>
        <SecretInputContainer>
          <SecretInput
            id="secret-value"
            value={value}
            onChange={handleValueChange}
            placeholder="Enter secret value"
            type={showValue ? "text" : "password"}
          />
          <ToggleVisibilityButton
            size="small"
            kind="alternative"
            disabled={value.length === 0 || value === SECRET_VALUE_PLACEHOLDER}
            onClick={() => setShowValue(!showValue)}
          >
            {showValue ? "Hide" : "Show"}
          </ToggleVisibilityButton>
        </SecretInputContainer>
        {valueError && <ErrorText>{valueError}</ErrorText>}
      </FormField>

      <Horizontal justifyContent="flex-end" gap="8px">
        <Button kind="alternative" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button kind="primary" onClick={handleSubmit} disabled={submitInProgress}>
          {submitInProgress ? ellipsize("Adding a secret") : "Add secret"}
        </Button>
      </Horizontal>
    </Vertical>
  )
}

const FormField = styled(Vertical)`
  gap: 8px;

  label {
    font-weight: 500;
  }
`

const ErrorText = styled.span`
  color: var(--error-color);
  font-size: 0.875rem;
`

const SecretInputContainer = styled(Horizontal)`
  position: relative;
  width: 100%;
`

const SecretInput = styled(Input)`
  padding-right: 60px;
`

const ToggleVisibilityButton = styled(Button)`
  position: absolute;
  right: 16px;
  top: 50%;

  transform: translateY(-50%);

  height: auto;
  border: none;
  padding: 0;

  ${smallFontSize};

  color: var(--secondary-text-color);

  &:hover:not(:disabled) {
    color: var(--secondary-text-color);
    background: var(--background-color);
  }
`
